import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ProductPreviewCard } from '@widgets/product-preview'

import { Product } from '@shared/generated'
import { useAnalytics } from '@shared/lib/analytics'
import Button from '@shared/ui/button'
import CardSkeleton from '@shared/ui/card-skeleton'

import styles from './styles.module.scss'


interface ProductCarouselProps {
  title?: string;
  products: Product[];
  link?: string;
  loading?: boolean;
  swiperClassName?: string;
  /** Название списка для аналитики (например: "Popular Products", "New Products") */
  listName?: string;
  /** ID списка для аналитики */
  listId?: string;
}

function ProductsLoading () {
  return (
    <div className={styles.skeleton}>
      {Array.from({ length: 8 }).map((_, index) => (
        <CardSkeleton key={index} />
      ))}
    </div>
  )
}

export function ProductCarousel (props: ProductCarouselProps) {
  const { title, products, link, loading, swiperClassName, listName, listId } = props
  const { t: tc } = useTranslation('common')
  const { viewItemList } = useAnalytics()
  const hasTrackedRef = useRef(false)

  // Track view_item_list when products are loaded (only once)
  useEffect(() => {
    if (products?.length > 0 && !hasTrackedRef.current) {
      hasTrackedRef.current = true
      viewItemList(products, listName ?? title ?? 'Product Carousel', listId)
    }
  }, [ products, listName, listId, title, viewItemList ])

  if (loading) {
    return <ProductsLoading />
  }

  if (!products?.length) {
    return null
  }

  return (
    <section className={styles.root}>
      {(title || link) && (
        <div className={styles.root__top}>
          {title && <h2 className={styles.root__title}>{title}</h2>}
          {link && <Button to={link} theme="grey">{tc('more')}</Button>}
        </div>
      )}

      <Swiper
        spaceBetween={8}
        slidesPerGroup={2}
        slidesPerView="auto"
        modules={[ Navigation ]}
        navigation={true}
        className={swiperClassName}
      >
        {products.map((p) => {
          return (
            <SwiperSlide className={styles.slide} key={p.id}>
              <ProductPreviewCard data={p as Product} />
            </SwiperSlide>
          )
        })}
      </Swiper>
    </section>
  )
}
